import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import ElevatedButton from '../components/Layout/ElevatedButton';
import SubscriptionModal from '../components/Layout/SubscriptionModal';
import { API_ENDPOINTS } from '../api/config';
import { calculateOverallCurrentGPA } from '../utils/gpa';
import { getLocalStudyStats, addLocalStudyMinutes, isLocalhost } from '../utils/localStudyTracker';
import { isCompletedStatus, isInProgressStatus } from '../utils/assignments';
import { FaReddit, FaFacebook, FaLinkedin } from 'react-icons/fa';
import PublicFooter from '../components/Layout/PublicFooter';
import LeaderboardModal from '../components/Dashboard/StudyLeaderboardModal';
import { getStudyLeaderboard } from '../services/studyLeaderboard';
import packageJson from '../../package.json';

const getLocalIsoDate = (date = new Date()) => {
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return localDate.toISOString().slice(0, 10);
};

const Container = styled.div`
  display: flex;
  flex: 1;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  width: 100%;
  overflow: hidden;
`;

const Sidebar = styled.div`
  width: 280px;
  background: ${props => props.theme.backgroundSecondary};
  border-right: 1px solid ${props => props.theme.border};
  padding: 1rem;
  display: flex;
  flex-direction: column;
  overflow-y: hidden;

  @media (max-width: 768px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid ${props => props.theme.border};
    padding: 0.75rem;
    order: 2;
    display: none;
  }
`;

const SidebarSection = styled.div`
  margin-bottom: 1.5rem;
`;

const SidebarTitle = styled.div`
  color: ${props => props.theme.text};
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 0.5rem;
  }
`;

const StatCard = styled.div`
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  padding: 1rem;
  text-align: center;
  margin-bottom: 0.75rem;
  min-height: ${props => props.minHeight || 'auto'};

  @media (max-width: 768px) {
    padding: 0.75rem;
    margin-bottom: 0.5rem;
  }
`;

const StatNumber = styled.div`
  color: ${props => props.color || props.theme.text};
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.25rem;

  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const StatLabel = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StudyTrackerCard = styled(StatCard)`
  text-align: left;
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;
  padding: 0.75rem;
  min-height: auto;
`;

const StudyTrackerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.5rem;
  flex-wrap: wrap;
  width: 100%;
  padding: 0;
  cursor: pointer;
`;

const StudyTrackerHeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  min-width: 0;
`;

const StudyTrackerTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const StudyTrackerActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: auto;
`;

const LeaderboardLinkButton = styled.button`
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.backgroundSecondary};
  color: ${props => props.theme.textSecondary};
  border-radius: 999px;
  padding: 0.3rem 0.65rem;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${props => props.theme.text};
    border-color: ${props => props.theme.buttonPrimary};
    background: ${props => props.theme.cardBackground};
  }
`;

const StudyTrackerToggleButton = styled.button`
  border: none;
  background: none;
  color: inherit;
  cursor: pointer;
  padding: 0.2rem 0.25rem;
  line-height: 1;

  &:focus,
  &:focus-visible {
    outline: none;
  }
`;

const StudyTrackerTitle = styled.h3`
  color: ${props => props.theme.text};
  font-size: 1rem;
  margin: 0;
  font-weight: 600;
`;

const StudyStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.75rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StudyStat = styled.div`
  background: ${props => props.theme.background};
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 0.65rem;
`;

const StudyStatNumber = styled.div`
  color: ${props => props.theme.text};
  font-size: 1.05rem;
  font-weight: 700;
  margin-bottom: 0.2rem;
`;

const StudyStatLabel = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.75rem;
  text-transform: uppercase;
`;

const StudyInputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const StudyInput = styled.input`
  width: 180px;
  min-width: 160px;
  border: 1px solid ${props => props.theme.border};
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  border-radius: 8px;
  padding: 0.6rem 0.8rem;
  font-size: 0.9rem;
  outline: none;

  &:focus {
    border-color: ${props => props.theme.primary};
  }
`;

const BadgePill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.primary};
  color: white;
  border-radius: 999px;
  padding: 0.35rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  margin-right: 0.5rem;
  margin-bottom: 0.5rem;
`;

const StudyBadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-top: 0.75rem;
`;

const StudyBadgeTitle = styled.div`
  color: ${props => props.theme.text};
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const BadgeSectionFooter = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  margin-top: 0.75rem;
  padding-bottom: 0.75rem;
`;

const StudyHelpText = styled.p`
  color: ${props => props.theme.textSecondary};
  margin: 0;
  font-size: 0.85rem;
`;

const StudyDropdownMenu = styled.div`
  max-height: ${props => props.$isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.25s ease;
  border-top: 1px solid ${props => props.theme.border};
  padding-top: ${props => props.$isOpen ? '0.75rem' : '0'};
`;

const StudyDropdownIcon = styled.span`
  display: inline-block;
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
  font-size: 0.9rem;
`;

const CommunityButtonsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CommunityDropdownHeader = styled.button`
  width: 100%;
  background: none;
  border: none;
  color: ${props => props.theme.text};
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 0.5rem 0;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    color: ${props => props.theme.primary};
  }

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  @media (max-width: 768px) {
    font-size: 0.8rem;
    margin-bottom: 0.5rem;
  }
`;

const CommunityDropdownIcon = styled.span`
  font-size: 0.75rem;
  transition: transform 0.2s;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0deg)'};
`;

const CommunityDropdownContent = styled.div`
  padding: ${props => props.isOpen ? '0.75rem' : '0'};
  max-height: ${props => props.isOpen ? '500px' : '0'};
  overflow: hidden;
  transition: all 0.3s ease-in-out;
`;

// const DiscordButton = styled.a`
//   display: inline-flex;
//   align-items: center;
//   justify-content: center;
//   gap: 0.5rem;
//   background: linear-gradient(135deg, #5865f2 0%, #7289da 50%, #5865f2 100%);
//   color: white;
//   padding: 0.5rem 0.75rem;
//   border-radius: 12px;
//   text-decoration: none;
//   font-weight: 600;
//   font-size: 0.75rem;
//   border: none;
//   cursor: pointer;
//   transition: all 0.3s ease;
//   width: 100%;
//   position: relative;
//   box-shadow: 0 2px 4px rgba(88, 101, 242, 0.2);

//   &:hover {
//     transform: translateY(-2px) rotate(1deg);
//     box-shadow: 0 6px 20px rgba(88, 101, 242, 0.4);
//     background: linear-gradient(135deg, #7289da 0%, #5865f2 50%, #7289da 100%);
//     animation: pulse 1.5s infinite;
//   }

//   @keyframes pulse {
//     0%, 100% { filter: brightness(1); }
//     50% { filter: brightness(1.1); }
//   }
// `;

const RedditButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #ff4500 0%, #ff6b35 100%);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 20px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.75rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  position: relative;

  &:hover {
    transform: translateY(-1px) scale(1.01) rotate(-1deg);
    box-shadow: 0 4px 12px rgba(255, 69, 0, 0.4);
    background: linear-gradient(135deg, #ff6b35 0%, #ff4500 100%);
  }
`;

const FacebookButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #1877f2 0%, #42a5f5 100%);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.75rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;

  &:hover {
    transform: translateY(-2px) rotate(1deg);
    box-shadow: 0 4px 15px rgba(24, 119, 242, 0.4);
    filter: brightness(1.1);
  }
`;

const LinkedInButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, #0077b5 0%, #005885 100%);
  color: white;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.75rem;
  border: 1px solid #005885;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;

  &:hover {
    transform: translateY(-1px) scale(1.02) rotate(-1deg);
    box-shadow: 0 3px 12px rgba(0, 119, 181, 0.3);
    border-color: #00a0dc;
  }
`;

const PaginationButton = styled.button`
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  color: ${props => props.theme.text};
  padding: 0.375rem 0.75rem;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover:not(:disabled) {
    background: ${props => props.theme.cardBackgroundHover};
  }
`;

const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;

  @media (max-width: 768px) {
    flex: 1;
    overflow-y: auto;
  }
`;

const DashboardHeader = styled.div`
  padding: 0.75rem;
  background: ${props => props.theme.background};

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const DashboardTitle = styled.h1`
  color: ${props => props.theme.text};
  font-size: 1.75rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin: 0;
  }
`;

const CurrentDate = styled.p`
  color: ${props => props.theme.textSecondary};
  margin: 0;
  font-size: 0.9375rem;

  @media (max-width: 768px) {
    font-size: 0.875rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    margin-top: 0.75rem;
    display: none;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
  }
`;

const CalendarSection = styled.div`
  padding: 1.5rem;
  display: flex;
  gap: 1.5rem;
  min-height: 650px;
  flex-shrink: 0;

  @media (min-width: 769px) {
    display: flex;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileContentSection = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    padding: 1rem;
  }
`;

const MobileSectionTitle = styled.div`
  color: ${props => props.theme.text};
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const UpcomingSidebar = styled.div`
  width: 280px;
  background: ${props => props.theme.backgroundSecondary};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  padding: 1rem;
  overflow-y: auto;

  @media (max-width: 768px) {
    width: 100%;
    max-height: 200px;
    padding: 0.75rem;
  }
`;

const UpcomingTitle = styled.h4`
  color: ${props => props.theme.text};
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 1rem 0;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin: 0 0 0.75rem 0;
  }
`;

const UpcomingItem = styled.div`
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  border-radius: 4px;
  padding: 0.75rem;
  margin-bottom: 0.75rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
    margin-bottom: 0.5rem;
  }
`;

const UpcomingItemTitle = styled.div`
  color: ${props => props.theme.text};
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const UpcomingItemMeta = styled.div`
  color: ${props => props.theme.textSecondary};
  font-size: 0.75rem;
  display: flex;
  justify-content: space-between;

  @media (max-width: 768px) {
    font-size: 0.7rem;
  }

  span:first-child {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    margin-right: 0.5rem;
  }
`;

const QuickStatsSection = styled(SidebarSection)`
  @media (max-width: 768px) {
    display: none;
  }
`;

const CalendarContainer = styled.div`
  flex: 1;
  background: ${props => props.theme.backgroundSecondary};
  border: 1px solid ${props => props.theme.border};
  border-radius: 6px;
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  max-width: 100%;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const CalendarTitle = styled.h4`
  color: ${props => props.theme.text};
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
`;

const CalendarNav = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const CalendarButton = styled.button`
  background: ${props => props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  color: ${props => props.theme.text};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  min-width: 32px;

  &:hover {
    background: ${props => props.theme.cardBackgroundHover};
    border-color: ${props => props.theme.buttonPrimary};
  }
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 0;
  border: 1px solid ${props => props.theme.border};
  flex: 1;
  max-width: 100%;
  height: 100%;
  grid-auto-rows: minmax(72px, 1fr);
`;

const CalendarDayHeader = styled.div`
  background: ${props => props.theme.backgroundSecondary};
  color: ${props => props.theme.textSecondary};
  font-size: 0.75rem;
  text-align: center;
  padding: 0.5rem;
  font-weight: 600;
  border: 1px solid ${props => props.theme.border};
`;

const CalendarDay = styled.div`
  background: ${props => props.$today ? props.theme.calendarToday : props.theme.cardBackground};
  border: 1px solid ${props => props.theme.border};
  min-height: 60px;
  padding: 0.25rem;
  position: relative;
  width: 100%;
  box-sizing: border-box;
`;

const CalendarDayNumber = styled.div`
  color: ${props => props.$today ? props.theme.text : props.theme.textSecondary};
  font-size: 0.75rem;
  font-weight: ${props => props.$today ? '600' : '400'};
  margin-bottom: 0.25rem;
`;

const CalendarEvent = styled.div`
  background: ${props => props.$color || '#5865f2'};
  color: ${props => props.theme.text};
  font-size: 0.625rem;
  padding: 0.125rem 0.25rem;
  border-radius: 2px;
  margin-bottom: 0.125rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
  box-sizing: border-box;
`;

const Dashboard = () => {
  const { user } = useAuth();
  const { assignments: realAssignments, courses, settings, loading, setLocalSettings } = useData();
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [upcomingAssignmentsPage, setUpcomingAssignmentsPage] = useState(0);
  const [sidebarAssignmentsPage, setSidebarAssignmentsPage] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [isCommunityOpen, setIsCommunityOpen] = useState(false);
  const [isStudyDropdownOpen, setIsStudyDropdownOpen] = useState(false);
  const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const [leaderboardError, setLeaderboardError] = useState('');
  const [studyStats, setStudyStats] = useState({
    totalMinutes: 0,
    totalPoints: 0,
    currentStreakDays: 0,
    longestStreakDays: 0,
    badges: [],
    skipDaysRemaining: 2,
    lastStudyDate: null
  });
  const [badgePage, setBadgePage] = useState(0);
  const [showBadgePages, setShowBadgePages] = useState(false);
  const [manualStudyMinutes, setManualStudyMinutes] = useState('');
  const [studyLoading, setStudyLoading] = useState(false);

  // Combine real and mock assignments (mock only in development)
  const assignments = [...(realAssignments || [])];

  const fetchStudyStats = async () => {
    if (isLocalhost()) {
      setStudyStats(getLocalStudyStats());
      return;
    }

    try {
      const response = await fetch(API_ENDPOINTS.STUDY_TRACKING, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.study) {
          setStudyStats(data.study);
        }
      }
    } catch (error) {
      console.error('Error fetching study stats:', error);
    }
  };

  useEffect(() => {
    if (isLocalhost()) {
      setStudyStats(getLocalStudyStats());
      return;
    }

    if (settings?.study) {
      setStudyStats(settings.study);
    } else if (!loading) {
      fetchStudyStats();
    }
  }, [settings, loading]);

  useEffect(() => {
    setBadgePage(0);
    setShowBadgePages(false);
  }, [studyStats.badges?.length]);

  useEffect(() => {
    let active = true;

    const warmLeaderboard = async () => {
      setLeaderboardLoading(true);
      setLeaderboardError('');

      try {
        const data = await getStudyLeaderboard(user?.name);
        if (active) {
          setLeaderboard(data);
        }
      } catch (error) {
        if (active) {
          setLeaderboardError('Unable to load leaderboard right now.');
        }
        console.error('Error fetching study leaderboard:', error);
      } finally {
        if (active) {
          setLeaderboardLoading(false);
        }
      }
    };

    warmLeaderboard();

    return () => {
      active = false;
    };
  }, [user?.name]);

  const logStudyMinutes = async (minutes) => {
    if (!minutes || Number(minutes) <= 0) {
      return;
    }

    setStudyLoading(true);

    if (isLocalhost()) {
      try {
        const updatedStudy = addLocalStudyMinutes(Number(minutes));
        setStudyStats(updatedStudy);
        setManualStudyMinutes('');
      } catch (error) {
        console.error('Error logging local study minutes:', error);
        alert('Unable to save study minutes locally. Please try again.');
      } finally {
        setStudyLoading(false);
      }
      return;
    }

    try {
      const payload = {
        minutes: Number(minutes),
        date: getLocalIsoDate()
      };
      const response = await fetch(API_ENDPOINTS.STUDY_TRACKING, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.study) {
          setStudyStats(data.study);
          setManualStudyMinutes('');
          // Also update DataContext settings locally so other components
          // that read `settings.study` reflect the change immediately
          // without issuing a network request.
          if (typeof setLocalSettings === 'function') {
            setLocalSettings(prev => ({ ...prev, study: data.study }));
          }
          // Update is applied locally from server response. Do not run a
          // full `prefetchData()` here — in production that can trigger
          // global auth handlers which may reload the page (e.g. on 401).
          // If broader sync is needed, it should be triggered by user
          // actions from higher-level UI, not this local logging flow.
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(errorData.message || 'Unable to log study minutes.');
      }
    } catch (error) {
      console.error('Error logging study minutes:', error);
      alert('Unable to log study minutes. Please try again.');
    } finally {
      setStudyLoading(false);
    }
  };

  const currentDate = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  // Check if they are active premium. If they are expired, cancelled, past due, or downgraded, they are NOT premium.
  const isPremium = (() => {
    if (!user) return false;

    // Check expiration
    const isExpired = user.subscriptionEndDate ? (() => {
      const endDate = new Date(user.subscriptionEndDate);
      endDate.setHours(12, 0, 0, 0); // Set to noon
      return new Date() >= endDate;
    })() : false;

    if (isExpired) return false;

    // Check cancellation/downgrade status
    const status = user.subscriptionStatus;
    const isCancelled = status === 'canceled' || status === 'cancelled' || status === 'past_due' || status === 'unpaid' || status === 'incomplete_expired' || status === 'incomplete';
    
    if (isCancelled || user.cancelAtPeriodEnd) return false;

    // Ensure we don't count 'free' or 'basic' as a paid tier
    const hasPaidPlan = user.subscription && user.subscription !== 'free' && user.subscription !== 'basic';
    const hasPaidAi = user.aiTier && user.aiTier !== 'free' && user.aiTier !== 'basic';

    return hasPaidPlan || hasPaidAi;
  })();

  // Calculate stats for all active courses and assignments
  // Filter courses to only active ones (not term-specific for broader stats)
  const activeCourses = courses?.filter(course =>
    course.status === 'active' || course.status === 'planned' || !course.status
  ) || [];
  
  const activeCourseIds = activeCourses.map(course => course._id || course.id);
  
  // Filter assignments to only those from active courses
  const activeAssignments = assignments?.filter(assignment => {
    const courseId = assignment.course?._id || assignment.course?.id || assignment.course;
    return activeCourseIds.some(id => String(id) === String(courseId));
  }) || [];
  
  const totalAssignments = activeAssignments.length;
  const completedAssignments = activeAssignments.filter(a => isCompletedStatus(a.status)).length;
  const inProgressAssignments = activeAssignments.filter(a => isInProgressStatus(a.status)).length;
  const completionRate = totalAssignments > 0 ? Math.round((completedAssignments / totalAssignments) * 100) : 0;

  // Overall stats across all courses and assignments
  const totalCoursesAllTime = courses?.length || 0;
  const totalAssignmentsAllTime = assignments?.length || 0;
  const avgAssignmentsPerCourse = totalCoursesAllTime > 0 ? Math.round(totalAssignmentsAllTime / totalCoursesAllTime) : 0;

  // Calculate GPA with error handling
  const currentGPA = (() => {
    try {
      const gpa = calculateOverallCurrentGPA(courses || [], assignments || [], settings);
      return isNaN(gpa) ? 0 : gpa;
    } catch (error) {
      console.error('Error calculating GPA:', error);
      return 0;
    }
  })();

  // Get upcoming assignments for the next 7 days
  const getUpcomingAssignmentsThisWeek = () => {
    if (!assignments) return [];
    
    const now = new Date();
    const sevenDaysFromNow = new Date(now);
    sevenDaysFromNow.setDate(now.getDate() + 7);
    
    return assignments
      .filter(a => {
        if (!a.due_date || isCompletedStatus(a.status)) return false;
        const dueDate = new Date(a.due_date);
        return dueDate >= now && dueDate <= sevenDaysFromNow;
      })
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  };

  // Get upcoming assignments from day 8 to day 90 (8-90 days)
  const getUpcomingAssignmentsLater = () => {
    if (!assignments) return [];
    
    const now = new Date();
    const eightDaysFromNow = new Date(now);
    eightDaysFromNow.setDate(now.getDate() + 8);
    const ninetyDaysFromNow = new Date(now);
    ninetyDaysFromNow.setDate(now.getDate() + 90);
    
    return assignments
      .filter(a => {
        if (!a.due_date || isCompletedStatus(a.status)) return false;
        const dueDate = new Date(a.due_date);
        return dueDate >= eightDaysFromNow && dueDate <= ninetyDaysFromNow;
      })
      .sort((a, b) => new Date(a.due_date) - new Date(b.due_date));
  };

  const upcomingAssignmentsThisWeek = getUpcomingAssignmentsThisWeek();
  const upcomingAssignmentsLater = getUpcomingAssignmentsLater();

  // Pagination constants
  const ITEMS_PER_PAGE = 5;

  // Pagination logic for upcoming assignments (8+ days - Due Later)
  const totalPages = Math.ceil(upcomingAssignmentsLater.length / ITEMS_PER_PAGE);
  const paginatedUpcomingAssignments = upcomingAssignmentsLater.slice(
    upcomingAssignmentsPage * ITEMS_PER_PAGE,
    (upcomingAssignmentsPage + 1) * ITEMS_PER_PAGE
  );

  // Sidebar pagination logic (4 per page)
  const SIDEBAR_ITEMS_PER_PAGE = 4;
  // Show all sidebar assignments to all users (no free/premium restriction)
  const sidebarAssignments = upcomingAssignmentsLater.slice();
  const sidebarTotalPages = Math.ceil(sidebarAssignments.length / SIDEBAR_ITEMS_PER_PAGE);
  const paginatedSidebarAssignments = sidebarAssignments.slice(
    sidebarAssignmentsPage * SIDEBAR_ITEMS_PER_PAGE,
    (sidebarAssignmentsPage + 1) * SIDEBAR_ITEMS_PER_PAGE
  );

  // Weekly assignments (show all upcoming assignments in next 7 days)
  const desktopWeeklyAssignments = upcomingAssignmentsThisWeek.slice();

  // Helper function to get course name
  const getCourseName = (assignment) => {
    if (!assignment.course) return 'Unknown Course';
    // Get the course ID whether assignment.course is an object or string
    const courseId = typeof assignment.course === 'object' ? assignment.course?._id : assignment.course;
    const course = courses.find(c => c._id === courseId);
    return course?.name || course?.course_code || 'Unknown Course';
  };

  // Get current term courses count (already calculated above as activeCourses)

  // Calendar functions
  const getDayHeaders = () => {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const weekStartsOn = settings?.preferences?.weekStart === 'monday' ? 1 : 0;
    return [...days.slice(weekStartsOn), ...days.slice(0, weekStartsOn)];
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    
    // Use user's week start setting (0 = Sunday, 1 = Monday)
    const weekStartsOn = settings?.preferences?.weekStart === 'monday' ? 1 : 0;
    const startingDayOfWeek = (firstDay.getDay() - weekStartsOn + 7) % 7;
    
    const days = [];
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add all days in month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    // Fill remaining cells to complete the grid (6 weeks)
    const totalCells = 42; // 6 weeks * 7 days
    const remainingCells = totalCells - days.length;
    for (let i = 0; i < remainingCells; i++) {
      days.push(null);
    }
    return days;
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentMonth(newDate);
  };

  const getAssignmentsForDay = (day) => {
    if (!day) return [];
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    return assignments?.filter(a => {
      const dueDate = new Date(a.due_date);
      return dueDate.getFullYear() === year &&
             dueDate.getMonth() === month &&
             dueDate.getDate() === day;
    }) || [];
  };

  const isToday = (day) => {
    if (!day) return false;
    const today = new Date();
    return today.getFullYear() === currentMonth.getFullYear() &&
           today.getMonth() === currentMonth.getMonth() &&
           today.getDate() === day;
  };

  const calendarDays = getDaysInMonth(currentMonth);

  // Avoid blocking the entire dashboard UI while data loads — render the
  // dashboard shell immediately and show a small non-blocking loading
  // indicator so users can still interact with the page.

  return (
    <Container>
      <Sidebar>
        <SidebarSection>
          <SidebarTitle title="Assignments due 8 or more days from today">Due Later</SidebarTitle>
          {paginatedSidebarAssignments.map(assignment => (
            <UpcomingItem 
              key={assignment._id} 
              title={`${assignment.title} - ${getCourseName(assignment)} - Due: ${new Date(assignment.due_date).toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}`}
            >
              <UpcomingItemTitle>{assignment.title}</UpcomingItemTitle>
              <UpcomingItemMeta>
                <span>{getCourseName(assignment)}</span>
                <span>
                  {new Date(assignment.due_date).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })}
                </span>
              </UpcomingItemMeta>
            </UpcomingItem>
          ))}
          {sidebarAssignments.length === 0 && (
            <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', textAlign: 'center', padding: '1rem' }}>
              No assignments due later
            </div>
          )}
          
          {/* Pagination controls for sidebar */}
          {sidebarTotalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem', marginTop: '0.75rem', paddingTop: '0.5rem', borderTop: '1px solid var(--color-border)' }}>
              <button
                onClick={() => setSidebarAssignmentsPage(Math.max(0, sidebarAssignmentsPage - 1))}
                disabled={sidebarAssignmentsPage === 0}
                style={{
                  background: sidebarAssignmentsPage === 0 ? 'var(--color-background-secondary)' : '#5865f2',
                  color: 'var(--color-text)',
                  border: 'none',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  cursor: sidebarAssignmentsPage === 0 ? 'not-allowed' : 'pointer',
                  opacity: sidebarAssignmentsPage === 0 ? 0.5 : 1
                }}
              >
                ‹
              </button>
              <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>
                Page {sidebarAssignmentsPage + 1} of {sidebarTotalPages}
              </span>
              <button
                onClick={() => setSidebarAssignmentsPage(Math.min(sidebarTotalPages - 1, sidebarAssignmentsPage + 1))}
                disabled={sidebarAssignmentsPage === sidebarTotalPages - 1}
                style={{
                  background: sidebarAssignmentsPage === sidebarTotalPages - 1 ? 'var(--color-background-secondary)' : '#5865f2',
                  color: 'var(--color-text)',
                  border: 'none',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  cursor: sidebarAssignmentsPage === sidebarTotalPages - 1 ? 'not-allowed' : 'pointer',
                  opacity: sidebarAssignmentsPage === sidebarTotalPages - 1 ? 0.5 : 1
                }}
              >
                ›
              </button>
            </div>
          )}
          

        </SidebarSection>

        <QuickStatsSection>
          <SidebarTitle>Quick Overview</SidebarTitle>
          <StatCard>
            <StatNumber color="#28a745">{totalAssignmentsAllTime}</StatNumber>
            <StatLabel>Total Assignments</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber color="#5865f2">{totalCoursesAllTime}</StatNumber>
            <StatLabel>Total Courses</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber color="#dc3545">{avgAssignmentsPerCourse}</StatNumber>
            <StatLabel>Avg Assignments/Course</StatLabel>
          </StatCard>
        </QuickStatsSection>

        <SidebarSection style={{ paddingTop: '0.5rem' }}>
          <CommunityDropdownHeader
            onClick={() => setIsCommunityOpen(!isCommunityOpen)}
            aria-expanded={isCommunityOpen}
          >
            Join our Community
            <CommunityDropdownIcon isOpen={isCommunityOpen}>▼</CommunityDropdownIcon>
          </CommunityDropdownHeader>
          <CommunityDropdownContent isOpen={isCommunityOpen}>
            <CommunityButtonsContainer>
              <FacebookButton href="https://www.facebook.com/profile.php?id=61585644187473" target="_blank" rel="noopener noreferrer">
                <FaFacebook />
                Follow on Facebook
              </FacebookButton>
              <LinkedInButton href="https://www.linkedin.com/company/planmysemester/" target="_blank" rel="noopener noreferrer">
                <FaLinkedin />
                Follow on LinkedIn
              </LinkedInButton>
              {/* <DiscordButton href="https://discord.gg/4kvNs3du6E" target="_blank" rel="noopener noreferrer">
                <FaDiscord />
                Join Discord
              </DiscordButton> */}
              <RedditButton href="https://www.reddit.com/r/PlanMySemester" target="_blank" rel="noopener noreferrer">
                <FaReddit />
                Join Subreddit
              </RedditButton>
            </CommunityButtonsContainer>
          </CommunityDropdownContent>
        </SidebarSection>
      </Sidebar>

      <MainContent>
        <DashboardHeader>
          <HeaderTop>
            <div>
                <DashboardTitle>Dashboard Overview <span style={{ fontSize: '0.6em', fontWeight: '400', color: 'var(--color-text-secondary)' }}>PlanMySemester Beta v{packageJson.version} - (March 29th, 2026)</span></DashboardTitle>
                {loading && (
                  <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem', marginTop: '6px' }}>Loading data…</div>
                )}
              <CurrentDate>{currentDate}</CurrentDate>
            </div>
            {!isPremium && (
              <ElevatedButton onClick={() => setIsSubscriptionModalOpen(true)}>
                Subscribe to Premium
              </ElevatedButton>
            )}
          </HeaderTop>
          <StatsGrid>
            <StatCard>
              <StatNumber color="#17a2b8">{currentGPA.toFixed(2)}</StatNumber>
              <StatLabel>Current GPA</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber color="#5865f2">{completionRate}%</StatNumber>
              <StatLabel>Completion Rate</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber color="#28a745">{completedAssignments}</StatNumber>
              <StatLabel>Completed Assignments</StatLabel>
            </StatCard>
            <StatCard>
              <StatNumber color="#ffc107">{inProgressAssignments}</StatNumber>
              <StatLabel>In Progress Assignments</StatLabel>
            </StatCard>
          </StatsGrid>
          <StudyTrackerCard>
            <StudyTrackerHeader
              role="group"
              tabIndex={0}
              onClick={() => setIsStudyDropdownOpen(prev => !prev)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setIsStudyDropdownOpen(prev => !prev);
                }
              }}
            >
              <StudyTrackerHeaderContent>
                <StudyTrackerTitleRow>
                  <StudyTrackerTitle>Study Tracker</StudyTrackerTitle>
                  <LeaderboardLinkButton
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setIsLeaderboardOpen(true);
                    }}
                  >
                    Leaderboard
                  </LeaderboardLinkButton>
                </StudyTrackerTitleRow>
                <StudyHelpText>
                  {studyStats.totalPoints} points · {studyStats.currentStreakDays} day streak · {studyStats.skipDaysRemaining} skips left
                </StudyHelpText>
              </StudyTrackerHeaderContent>
              <StudyTrackerActions>
                <StudyTrackerToggleButton
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setIsStudyDropdownOpen(prev => !prev);
                  }}
                  aria-expanded={isStudyDropdownOpen}
                  aria-label={isStudyDropdownOpen ? 'Collapse study tracker details' : 'Expand study tracker details'}
                >
                  <StudyDropdownIcon $isOpen={isStudyDropdownOpen}>
                    {isStudyDropdownOpen ? '−' : '+'}
                  </StudyDropdownIcon>
                </StudyTrackerToggleButton>
              </StudyTrackerActions>
            </StudyTrackerHeader>
            <StudyDropdownMenu $isOpen={isStudyDropdownOpen}>
              <StudyStatsGrid>
                <StudyStat>
                  <StudyStatNumber>{studyStats.totalPoints}</StudyStatNumber>
                  <StudyStatLabel>Points</StudyStatLabel>
                </StudyStat>
                <StudyStat>
                  <StudyStatNumber>{(studyStats.totalMinutes / 60).toFixed(1)}</StudyStatNumber>
                  <StudyStatLabel>Hours Studied</StudyStatLabel>
                </StudyStat>
                <StudyStat>
                  <StudyStatNumber>{studyStats.currentStreakDays}</StudyStatNumber>
                  <StudyStatLabel>Current Streak</StudyStatLabel>
                </StudyStat>
              </StudyStatsGrid>
              <StudyStatsGrid>
                <StudyStat>
                  <StudyStatNumber>{studyStats.longestStreakDays}</StudyStatNumber>
                  <StudyStatLabel>Longest Streak</StudyStatLabel>
                </StudyStat>
                <StudyStat>
                  <StudyStatNumber>{studyStats.skipDaysRemaining}</StudyStatNumber>
                  <StudyStatLabel>Skip Days Left</StudyStatLabel>
                </StudyStat>
                <StudyStat>
                  <StudyStatNumber>{studyStats.badges?.length || 0}</StudyStatNumber>
                  <StudyStatLabel>Badges Earned</StudyStatLabel>
                </StudyStat>
              </StudyStatsGrid>
              <StudyBadgeTitle>Most Recent Badges</StudyBadgeTitle>
              <StudyBadgeRow>
                {studyStats.badges?.length > 0 ? (
                  (() => {
                    const sortedBadges = [...studyStats.badges].reverse();
                    const visibleBadges = sortedBadges.slice(0, 5);
                    return visibleBadges.map(badge => (
                      <BadgePill key={badge}>{badge}</BadgePill>
                    ));
                  })()
                ) : (
                  <StudyHelpText>Earn your first streak badge with 3 study days.</StudyHelpText>
                )}
              </StudyBadgeRow>

              {studyStats.badges?.length > 5 && (
                <>
                  <BadgeSectionFooter>
                    <ElevatedButton
                      onClick={() => setShowBadgePages(prev => !prev)}
                      style={{ padding: '0.55rem 0.9rem', fontSize: '0.75rem', marginBottom: '0.5rem' }}
                    >
                      {showBadgePages ? 'Hide older badges' : `Show ${Math.min(10, studyStats.badges.length - 5)} more badges`}
                    </ElevatedButton>
                    {showBadgePages && (
                      <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.8rem' }}>
                        Showing {badgePage * 10 + 1} - {Math.min((badgePage + 1) * 10, studyStats.badges.length - 5)} of {studyStats.badges.length - 5} older badges
                      </span>
                    )}
                  </BadgeSectionFooter>
                </>
              )}

              {showBadgePages && studyStats.badges?.length > 5 && (
                (() => {
                  const sortedBadges = [...studyStats.badges].reverse();
                  const extraBadges = sortedBadges.slice(5);
                  const badgePageSize = 10;
                  const pagedBadges = extraBadges.slice(badgePage * badgePageSize, (badgePage + 1) * badgePageSize);

                  return (
                    <>
                      <StudyBadgeRow>
                        {pagedBadges.map(badge => (
                          <BadgePill key={badge}>{badge}</BadgePill>
                        ))}
                      </StudyBadgeRow>
                      <BadgeSectionFooter>
                        <ElevatedButton
                          disabled={badgePage === 0}
                          onClick={() => setBadgePage(prev => Math.max(prev - 1, 0))}
                          style={{ padding: '0.55rem 0.9rem', fontSize: '0.75rem', marginBottom: '0.5rem' }}
                        >
                          Previous
                        </ElevatedButton>
                        <ElevatedButton
                          disabled={(badgePage + 1) * 10 >= (studyStats.badges.length - 5)}
                          onClick={() => setBadgePage(prev => prev + 1)}
                          style={{ padding: '0.55rem 0.9rem', fontSize: '0.75rem', marginBottom: '0.5rem' }}
                        >
                          Next
                        </ElevatedButton>
                      </BadgeSectionFooter>
                    </>
                  );
                })()
              )}

              <StudyInputRow>
                <StudyInput
                  type="number"
                  min="1"
                  value={manualStudyMinutes}
                  onChange={e => setManualStudyMinutes(e.target.value)}
                  placeholder="Minutes studied"
                  aria-label="Minutes studied"
                />
                <ElevatedButton
                  onClick={() => logStudyMinutes(manualStudyMinutes)}
                  disabled={studyLoading || !manualStudyMinutes}
                >
                  Log Minutes
                </ElevatedButton>
                <ElevatedButton
                  onClick={() => logStudyMinutes(15)}
                  disabled={studyLoading}
                >+15 min</ElevatedButton>
                <ElevatedButton
                  onClick={() => logStudyMinutes(30)}
                  disabled={studyLoading}
                >+30 min</ElevatedButton>
              </StudyInputRow>
              <StudyHelpText>Each 30 minutes of study earns 1 point. You can skip 2 days each week without breaking your streak.</StudyHelpText>
            </StudyDropdownMenu>
          </StudyTrackerCard>
        </DashboardHeader>

        <CalendarSection>
          <CalendarContainer>
            <CalendarHeader>
              <CalendarTitle>
                {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </CalendarTitle>
              <CalendarNav>
                <CalendarButton onClick={() => navigateMonth(-1)}>‹</CalendarButton>
                <CalendarButton onClick={() => navigateMonth(1)}>›</CalendarButton>
              </CalendarNav>
            </CalendarHeader>
            <CalendarGrid>
              {getDayHeaders().map(day => (
                <CalendarDayHeader key={day}>{day}</CalendarDayHeader>
              ))}
              
              {calendarDays.map((day, index) => {
                const dayAssignments = getAssignmentsForDay(day);
                return (
                  <CalendarDay 
                    key={index} 
                    $today={isToday(day)}
                  >
                    {day && (
                      <>
                        <CalendarDayNumber $today={isToday(day)}>
                          {day}
                        </CalendarDayNumber>
                        {dayAssignments.slice(0, 2).map((assignment, idx) => (
                          <CalendarEvent 
                            key={idx}
                            $color={assignment.course?.color || '#5865f2'}
                            title={`${assignment.course?.course_code ? `${assignment.course.course_code}: ` : (assignment.course?.name ? `${assignment.course.name}: ` : '')}${assignment.title}`}
                          >
                            <div style={{ fontWeight: 'bold', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {assignment.course?.course_code ? `${assignment.course.course_code}: ` : (assignment.course?.name ? `${assignment.course.name}: ` : '')}{assignment.title}
                            </div>
                          </CalendarEvent>
                        ))}
                        {dayAssignments.length > 2 && (
                          <CalendarEvent $color="#6c757d">
                            +{dayAssignments.length - 2} more
                          </CalendarEvent>
                        )}
                      </>
                    )}
                  </CalendarDay>
                );
              })}
            </CalendarGrid>
          </CalendarContainer>

          <UpcomingSidebar>
            <UpcomingTitle title="Assignments due in the next 7 days">Next 7 Days</UpcomingTitle>
            {desktopWeeklyAssignments.map(assignment => (
              <UpcomingItem 
                key={assignment._id} 
                title={`${assignment.title} - ${getCourseName(assignment)} - Due: ${new Date(assignment.due_date).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}`}
              >
                <UpcomingItemTitle>{assignment.title}</UpcomingItemTitle>
                <UpcomingItemMeta>
                  <span>{getCourseName(assignment)}</span>
                  <span>
                    {new Date(assignment.due_date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                </UpcomingItemMeta>
              </UpcomingItem>
            ))}
            {upcomingAssignmentsThisWeek.length === 0 && (
              <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', textAlign: 'center', padding: '1rem' }}>
                No assignments this week
              </div>
            )}
            

            

          </UpcomingSidebar>
        </CalendarSection>

        {/* Upcoming Assignments Section - Below Calendar on Desktop */}
        <div style={{ padding: '1.5rem', display: 'none' }}>
          {/* Upcoming Assignments - Integrated into main flow */}
          <div style={{ marginTop: '2rem' }}>
            <div style={{ color: 'var(--color-text)', fontSize: '1.125rem', fontWeight: '600', marginBottom: '1rem', textTransform: 'uppercase', letterSpacing: '0.5px' }} title="Assignments due 8 or more days from today">
              Due Later
            </div>
            {paginatedUpcomingAssignments.length > 0 ? (
              paginatedUpcomingAssignments.map(assignment => (
                <div 
                  key={assignment._id} 
                  style={{ background: 'var(--color-card-background)', border: '1px solid var(--color-border)', borderRadius: '4px', padding: '0.75rem', marginBottom: '0.75rem' }}
                  title={`${assignment.title} - ${getCourseName(assignment)} - Due: ${new Date(assignment.due_date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}`}
                >
                  <div style={{ color: 'var(--color-text)', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.25rem' }}>
                    {assignment.title}
                  </div>
                  <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem', display: 'flex', justifyContent: 'space-between' }}>
                    <span>{getCourseName(assignment)}</span>
                    <span style={{
                      color: (() => {
                        const dueDate = new Date(assignment.due_date);
                        const today = new Date();
                        const diffTime = dueDate - today;
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                        if (diffDays < 0) return '#dc3545'; // Overdue
                        if (diffDays <= 1) return '#ffc107'; // Due soon
                        return '#28a745'; // Due later
                      })()
                    }}>
                      {(() => {
                        const dueDate = new Date(assignment.due_date);
                        const today = new Date();
                        const diffTime = dueDate - today;
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                        if (diffDays < 0) return 'Overdue';
                        if (diffDays === 0) return 'Today';
                        if (diffDays === 1) return 'Tomorrow';
                        return `In ${diffDays} days`;
                      })()}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', textAlign: 'center', padding: '1rem' }}>
                No assignments due later
              </div>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
                <PaginationButton
                  onClick={() => setUpcomingAssignmentsPage(Math.max(0, upcomingAssignmentsPage - 1))}
                  disabled={upcomingAssignmentsPage === 0}
                >
                  Previous
                </PaginationButton>
                <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>
                  Page {upcomingAssignmentsPage + 1} of {totalPages}
                </div>
                <PaginationButton
                  onClick={() => setUpcomingAssignmentsPage(Math.min(totalPages - 1, upcomingAssignmentsPage + 1))}
                  disabled={upcomingAssignmentsPage === totalPages - 1}
                >
                  Next
                </PaginationButton>
              </div>
            )}
          </div>
        </div>

        {/* Mobile-Only Content: Upcoming This Week + Upcoming Assignments */}
        <MobileContentSection>
          {/* Upcoming This Week - First on mobile */}
          <div style={{ marginBottom: '2rem' }}>
            <MobileSectionTitle title="Assignments due in the next 7 days">Next 7 Days</MobileSectionTitle>
            {desktopWeeklyAssignments.map(assignment => (
              <div 
                key={assignment._id} 
                style={{ background: 'var(--color-card-background)', border: '1px solid var(--color-border)', borderRadius: '4px', padding: '0.75rem', marginBottom: '0.75rem' }}
                title={`${assignment.title} - ${getCourseName(assignment)} - Due: ${new Date(assignment.due_date).toLocaleDateString('en-US', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}`}
              >
                <div style={{ color: 'var(--color-text)', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.25rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {assignment.title}
                </div>
                <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, marginRight: '0.5rem' }}>{getCourseName(assignment)}</span>
                  <span>
                    {new Date(assignment.due_date).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              </div>
            ))} 
            {upcomingAssignmentsThisWeek.length === 0 && (
              <div style={{ color: '#a0a0a0', fontSize: '0.875rem', textAlign: 'center', padding: '1rem' }}>
                No assignments this week
              </div>
            )}
            

          </div>

          {/* Upcoming Assignments - Right below Upcoming This Week */}
          <div>
            <MobileSectionTitle title="Assignments due 8 or more days from today">Due Later</MobileSectionTitle>
            {paginatedUpcomingAssignments.length > 0 ? (
              paginatedUpcomingAssignments.map(assignment => (
                <div 
                  key={assignment._id} 
                  style={{ background: 'var(--color-card-background)', border: '1px solid var(--color-border)', borderRadius: '4px', padding: '0.75rem', marginBottom: '0.75rem' }}
                  title={`${assignment.title} - ${getCourseName(assignment)} - Due: ${new Date(assignment.due_date).toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}`}
                >
                  <div style={{ color: 'var(--color-text)', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.25rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {assignment.title}
                  </div>
                  <div style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem', display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, marginRight: '0.5rem' }}>{getCourseName(assignment)}</span>
                    <span style={{
                      color: (() => {
                        const dueDate = new Date(assignment.due_date);
                        const today = new Date();
                        const diffTime = dueDate - today;
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                        if (diffDays < 0) return '#dc3545';
                        if (diffDays <= 1) return '#ffc107';
                        return '#28a745';
                      })()
                    }}>
                      {(() => {
                        const dueDate = new Date(assignment.due_date);
                        const today = new Date();
                        const diffTime = dueDate - today;
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

                        if (diffDays < 0) return 'Overdue';
                        if (diffDays === 0) return 'Today';
                        if (diffDays === 1) return 'Tomorrow';
                        return `In ${diffDays} days`;
                      })()}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.875rem', textAlign: 'center', padding: '1rem' }}>
                No assignments due later
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--color-border)' }}>
                <PaginationButton
                  onClick={() => setUpcomingAssignmentsPage(Math.max(0, upcomingAssignmentsPage - 1))}
                  disabled={upcomingAssignmentsPage === 0}
                >
                  Previous
                </PaginationButton>
                <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.75rem' }}>
                  Page {upcomingAssignmentsPage + 1} of {totalPages}
                </div>
                <PaginationButton
                  onClick={() => setUpcomingAssignmentsPage(Math.min(totalPages - 1, upcomingAssignmentsPage + 1))}
                  disabled={upcomingAssignmentsPage === totalPages - 1}
                >
                  Next
                </PaginationButton>
              </div>
            )}
          </div>
          <PublicFooter />
        </MobileContentSection>
        <PublicFooter />
      </MainContent>

      <SubscriptionModal
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
      />

      <LeaderboardModal
        isOpen={isLeaderboardOpen}
        onClose={() => setIsLeaderboardOpen(false)}
        leaderboard={leaderboard}
        loading={leaderboardLoading}
        error={leaderboardError}
      />
    </Container>
  );
};

export default Dashboard;
